import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import {
  ChevronLeft, ChevronRight, ChevronDown, Activity, Coffee, Camera, Percent, LogIn, LogOut, HelpCircle, AppWindow, Download,
} from 'lucide-react';
import { Card, StatCard } from '@/components/Card';
import { StatusBadge } from '@/components/Badge';
import Loader, { EmptyState } from '@/components/Loader';
import ConfirmDialog from '@/components/ConfirmDialog';
import ScreenshotLightbox from '@/components/ScreenshotLightbox';
import ScreenshotTile from '@/components/ScreenshotTile';
import { trackerService, userService, fileUrl } from '@/services';
import { fmtTime, secondsToHours, minutesToHours, activityColor, initials, download } from '@/utils/format';

const today = () => dayjs().format('YYYY-MM-DD');

export default function TrackerDay() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [date, setDate] = useState(today());

  const users = useQuery({ queryKey: ['users', 'all'], queryFn: () => userService.list({ limit: 200 }) });
  const [lightbox, setLightbox] = useState(null); // { items, index }
  const [confirmState, setConfirmState] = useState(null);
  const barRef = useRef(null);
  const [hoverSeg, setHoverSeg] = useState(null); // { seg, pct, topApp, x }
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onDown = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setPickerOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [pickerOpen]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tracker', 'day', userId, date],
    queryFn: () => trackerService.adminDay({ user: userId, date }),
  });

  const user = data?.user;
  const totals = data?.totals;
  const timeline = data?.timeline || [];
  const topApps = data?.topApps || [];
  const screenshots = data?.screenshots || [];
  const attendance = data?.attendance;

  const shiftDay = (days) => setDate((d) => dayjs(d).add(days, 'day').format('YYYY-MM-DD'));

  const removeShot = (shot) => {
    setConfirmState({
      title: 'Delete screenshot',
      message: 'This screenshot will be permanently removed from the server. This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          await trackerService.removeScreenshot(shot._id);
          toast.success('Screenshot deleted');
          setLightbox(null);
          qc.invalidateQueries({ queryKey: ['tracker'] });
        } catch (e) {
          toast.error(e.response?.data?.message || 'Failed');
        }
      },
    });
  };

  const maxAppSeconds = Math.max(1, ...topApps.map((a) => a.seconds || 0));
  const timelineTotal = Math.max(
    1,
    timeline.reduce((sum, t) => sum + Math.max(0, dayjs(t.to).diff(dayjs(t.from), 'second')), 0)
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {user?.avatar ? (
            <img src={fileUrl(user.avatar)} alt={user?.name} className="w-11 h-11 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center font-semibold shrink-0">
              {initials(user?.name)}
            </div>
          )}
          <div ref={pickerRef} className="relative min-w-0">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPickerOpen((v) => !v)}
                className="flex items-center gap-1.5 min-w-0 group"
                title="Switch employee"
              >
                <h1 className="text-xl font-bold truncate">{user?.name || 'Employee'}</h1>
                <ChevronDown size={18} className={`shrink-0 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200 transition ${pickerOpen ? 'rotate-180' : ''}`} />
              </button>
              {attendance?.status && (
                <StatusBadge status={attendance.isLate ? 'late' : attendance.status} />
              )}
            </div>
            <div className="text-sm text-slate-500 truncate">{user?.email}</div>

            {pickerOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 max-h-72 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-soft z-30 py-1">
                {(users.data?.items || []).map((u) => (
                  <button
                    key={u._id}
                    onClick={() => { setPickerOpen(false); navigate(`/admin/tracker/${u._id}`); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${
                      String(u._id) === String(userId) ? 'bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-300 font-medium' : ''
                    }`}
                  >
                    {u.name}
                    {u.employeeId && <span className="text-xs text-slate-400 ml-1">· {u.employeeId}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <button className="btn-secondary px-2 py-1.5" onClick={() => shiftDay(-1)} aria-label="Previous day">
            <ChevronLeft size={16} />
          </button>
          <input
            type="date"
            className="input w-44"
            value={date}
            max={today()}
            onChange={(e) => setDate(e.target.value || today())}
          />
          <button
            className="btn-secondary px-2 py-1.5"
            onClick={() => shiftDay(1)}
            disabled={date >= today()}
            aria-label="Next day"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {isError ? (
        <Card>
          <EmptyState
            title="Could not load this day"
            subtitle={error?.response?.data?.message || error?.message || 'Something went wrong'}
          />
        </Card>
      ) : isLoading ? (
        <Loader />
      ) : (
        <>
          <TrackerRangeSummary userId={userId} user={user} onOpenDay={(d) => setDate(d)} />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Activity} color="emerald" label="Active time" value={secondsToHours(totals?.activeSeconds ?? 0)} hint={`Tracked ${secondsToHours(totals?.trackedSeconds ?? 0)}`} />
            <StatCard icon={Coffee} color="amber" label="Idle time" value={secondsToHours(totals?.idleSeconds ?? 0)} />
            <StatCard icon={Percent} color="brand" label="Activity" value={`${Math.round(totals?.activityPercent ?? 0)}%`} />
            <StatCard icon={Camera} color="violet" label="Screenshots" value={totals?.screenshots ?? 0} />
            <StatCard icon={LogIn} color="sky" label="Check-in" value={fmtTime(attendance?.checkInAt)} hint={attendance?.isLate ? 'Late' : undefined} />
            <StatCard icon={LogOut} color="sky" label="Check-out" value={fmtTime(attendance?.checkOutAt)} hint={attendance ? minutesToHours(totals?.attendanceMinutes ?? 0) + ' on the clock' : undefined} />
            <StatCard
              icon={HelpCircle}
              color="rose"
              label="Away (untracked)"
              value={minutesToHours(totals?.awayMinutes ?? 0)}
              hint="Time between check-in and check-out that the tracker did not see"
            />
            <StatCard icon={AppWindow} color="brand" label="Online (tracked)" value={minutesToHours(totals?.onlineMinutes ?? 0)} />
            {/* Time the machine was asleep or locked that the employee said was
                work. It counts, but no keyboard proves it — so it is shown apart
                from measured active time rather than folded into it. */}
            {totals?.claimedSeconds > 0 && (
              <StatCard
                icon={Coffee}
                color="rose"
                label="Claimed (not measured)"
                value={secondsToHours(totals.claimedSeconds)}
                hint="Employee said this away time was work"
              />
            )}
          </div>

          <Card>
            <div className="font-semibold mb-1">Day timeline</div>
            <div className="text-xs text-slate-500 mb-4">
              {totals?.firstStart ? `${fmtTime(totals.firstStart)} — ${fmtTime(totals.lastSeen)}` : 'No tracked activity'}
              <span className="ml-3 inline-flex items-center gap-3">
                <LegendDot className="bg-rose-500" label="< 30%" />
                <LegendDot className="bg-amber-500" label="30–60%" />
                <LegendDot className="bg-emerald-500" label="> 60%" />
              </span>
            </div>

            {timeline.length === 0 ? (
              <EmptyState title="No tracker activity on this day" subtitle="The desktop tracker did not report any session." />
            ) : (
              // Outer wrapper is NOT clipped, so the hover tooltip can sit above the
              // bar; the coloured segments live in an inner overflow-hidden strip.
              <div ref={barRef} className="relative" onMouseLeave={() => setHoverSeg(null)}>
                <div className="flex h-10 w-full rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800">
                  {timeline.map((seg, i) => {
                    const secs = Math.max(1, dayjs(seg.to).diff(dayjs(seg.from), 'second'));
                    const pct = Math.round(seg.activityPercent || 0);
                    const topApp = (seg.apps || []).slice().sort((a, b) => (b.seconds || 0) - (a.seconds || 0))[0];
                    const track = (e) => {
                      const rect = barRef.current?.getBoundingClientRect();
                      setHoverSeg({ seg, pct, topApp, x: rect ? e.clientX - rect.left : 0 });
                    };
                    return (
                      <div
                        key={i}
                        onMouseEnter={track}
                        onMouseMove={track}
                        // Claimed time was never measured — the machine was asleep or
                        // locked and the employee said afterwards that it was work. It
                        // gets its own hatched, colourless band so it can never be
                        // mistaken for time the app actually observed.
                        className={`h-full border-r border-white/40 dark:border-slate-900/40 last:border-r-0 cursor-crosshair ${
                          seg.claimed
                            ? 'bg-slate-300 dark:bg-slate-600 bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(255,255,255,0.45)_4px,rgba(255,255,255,0.45)_8px)]'
                            : activityColor(pct)
                        }`}
                        style={{ width: `${(secs / timelineTotal) * 100}%` }}
                      />
                    );
                  })}
                </div>

                {hoverSeg && (
                  <div
                    className="pointer-events-none absolute z-30 bottom-full mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-slate-900 text-white text-xs px-3 py-2 shadow-soft"
                    style={{ left: `${Math.min(Math.max(hoverSeg.x, 60), (barRef.current?.offsetWidth || 0) - 60)}px` }}
                  >
                    <div className="font-semibold">
                      {fmtTime(hoverSeg.seg.from)} – {fmtTime(hoverSeg.seg.to)}
                    </div>
                    {hoverSeg.seg.claimed ? (
                      <>
                        <div className="text-amber-300">Claimed — machine asleep/locked, not measured</div>
                        <div>Counted {secondsToHours(hoverSeg.seg.activeSeconds)}</div>
                        {hoverSeg.seg.claimedReason && (
                          <div className="text-slate-300">“{hoverSeg.seg.claimedReason}”</div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="text-base font-bold text-emerald-300">{hoverSeg.pct}% active</div>
                        <div>
                          Active {secondsToHours(hoverSeg.seg.activeSeconds)} · Idle {secondsToHours(hoverSeg.seg.idleSeconds)}
                        </div>
                        <div className="text-slate-300">
                          {hoverSeg.topApp ? `${hoverSeg.topApp.name} (${secondsToHours(hoverSeg.topApp.seconds)})` : 'No app recorded'}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>

          <div className="grid lg:grid-cols-3 gap-4">
            <Card>
              <div className="font-semibold mb-3">Top applications</div>
              {topApps.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No application data</div>
              ) : (
                <div className="space-y-3">
                  {topApps.map((app) => (
                    <div key={app.name}>
                      <div className="flex items-center justify-between text-sm mb-1 gap-2">
                        <span className="font-medium truncate">{app.name || 'Unknown'}</span>
                        <span className="text-slate-500 shrink-0">{secondsToHours(app.seconds)}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-brand-500"
                          style={{ width: `${Math.round(((app.seconds || 0) / maxAppSeconds) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="lg:col-span-2">
              <div className="font-semibold mb-3 flex items-center gap-2">
                <Camera size={18} /> Screenshots
                <span className="text-sm font-normal text-slate-500">({screenshots.length})</span>
              </div>
              {screenshots.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No screenshots captured on this day</div>
              ) : (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {screenshots.map((shot, i) => (
                    <ScreenshotTile
                      key={shot._id}
                      shot={shot}
                      onClick={() => setLightbox({ items: screenshots, index: i })}
                    />
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}

      <ScreenshotLightbox
        state={lightbox}
        onClose={() => setLightbox(null)}
        onIndexChange={(i) => setLightbox((s) => ({ ...s, index: i }))}
        onDelete={removeShot}
      />
      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}

function LegendDot({ className, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`w-2 h-2 rounded-full ${className}`} /> {label}
    </span>
  );
}

// Date-range tracker sheet for one employee: per-day tracked/active/idle hours,
// with a CSV download. Admin picks the range (defaults to the current month).
function TrackerRangeSummary({ userId, user, onOpenDay }) {
  const token = useSelector((s) => s.auth.token);
  const [from, setFrom] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().format('YYYY-MM-DD'));
  const [active, setActive] = useState('this'); // 'this' | 'last' | null

  const { data, isLoading } = useQuery({
    queryKey: ['tracker', 'summary', userId, from, to],
    queryFn: () => trackerService.adminSummary({ user: userId, from, to }),
  });
  const days = data?.days || [];
  const totals = days.reduce(
    (acc, d) => {
      acc.active += d.activeSeconds;
      acc.idle += d.idleSeconds;
      acc.shots += d.screenshots || 0;
      return acc;
    },
    { active: 0, idle: 0, shots: 0 }
  );

  const setMonth = (offset) => {
    const m = dayjs().subtract(offset, 'month');
    setFrom(m.startOf('month').format('YYYY-MM-DD'));
    setTo(m.endOf('month').isAfter(dayjs()) ? dayjs().format('YYYY-MM-DD') : m.endOf('month').format('YYYY-MM-DD'));
    setActive(offset === 0 ? 'this' : 'last');
  };

  const exportCSV = () => {
    const who = (user?.employeeId || user?.name || 'user').replace(/[^a-z0-9_-]/gi, '_');
    download(`tracker/admin/summary.csv?user=${userId}&from=${from}&to=${to}`, `tracker_${who}_${from}_to_${to}.csv`, token);
  };

  return (
    <Card>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${
              active === 'this'
                ? 'bg-brand-600 text-white border-brand-600'
                : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
            }`}
            onClick={() => setMonth(0)}
          >
            This month
          </button>
          <button
            className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${
              active === 'last'
                ? 'bg-brand-600 text-white border-brand-600'
                : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
            }`}
            onClick={() => setMonth(1)}
          >
            Last month
          </button>

          <div className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 pl-3 pr-1 h-[42px] focus-within:border-brand-400">
            <span className="text-xs font-semibold text-slate-500 uppercase">From</span>
            <input
              type="date"
              className="bg-transparent border-0 outline-none text-sm py-0"
              value={from}
              max={to}
              onChange={(e) => { setFrom(e.target.value); setActive(null); }}
            />
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 pl-3 pr-1 h-[42px] focus-within:border-brand-400">
            <span className="text-xs font-semibold text-slate-500 uppercase">To</span>
            <input
              type="date"
              className="bg-transparent border-0 outline-none text-sm py-0"
              value={to}
              min={from}
              max={dayjs().format('YYYY-MM-DD')}
              onChange={(e) => { setTo(e.target.value); setActive(null); }}
            />
          </div>
        </div>

        <button className="btn-secondary" onClick={exportCSV} disabled={days.length === 0}>
          <Download size={14} /> Download CSV
        </button>
      </div>

      {isLoading ? (
        <Loader />
      ) : days.length === 0 ? (
        <EmptyState title="No tracker activity in this range" subtitle="Pick another date range or check a different month." />
      ) : (
        <div className="table-wrap max-h-[18rem] overflow-y-auto">
          <table className="min-w-full [&_.td]:!py-4">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50 dark:bg-slate-800/90 backdrop-blur">
                <th className="th">Date</th>
                <th className="th">Tracked</th>
                <th className="th">Active</th>
                <th className="th">Idle</th>
                <th className="th">Activity</th>
                <th className="th">Shots</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr
                  key={d.date}
                  className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  onClick={() => onOpenDay?.(d.date)}
                  title="Open this day"
                >
                  <td className="td font-medium">{dayjs(d.date).format('DD MMM (ddd)')}</td>
                  <td className="td whitespace-nowrap">{secondsToHours(d.activeSeconds + d.idleSeconds)}</td>
                  <td className="td whitespace-nowrap">{secondsToHours(d.activeSeconds)}</td>
                  <td className="td whitespace-nowrap text-slate-500">{secondsToHours(d.idleSeconds)}</td>
                  <td className="td">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                        <div className={`h-full rounded-full ${activityColor(d.activityPercent)}`} style={{ width: `${d.activityPercent}%` }} />
                      </div>
                      <span className="text-xs text-slate-500">{d.activityPercent}%</span>
                    </div>
                  </td>
                  <td className="td">{d.screenshots || 0}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0 z-10">
              <tr className="border-t-2 border-slate-200 dark:border-slate-700 font-semibold bg-slate-50 dark:bg-slate-800/90 backdrop-blur">
                <td className="td">Total <span className="text-slate-500 font-normal">· {days.length} {days.length === 1 ? 'day' : 'days'}</span></td>
                <td className="td">{secondsToHours(totals.active + totals.idle)}</td>
                <td className="td">{secondsToHours(totals.active)}</td>
                <td className="td text-slate-500">{secondsToHours(totals.idle)}</td>
                <td className="td" />
                <td className="td">{totals.shots}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  );
}

